import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Radio,
  Select,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { ResidualRisk } from '../../../api/workEnvironmentTypes';
import {
  useChangeNextSurveyMutation,
  useDecideResidualRiskMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import {
  decisionText,
  documentedDecision,
  draftErrors,
  earlierDecisionText,
  followsNextSurvey,
  nextSurveyErrors,
  nextSurveyText,
  ownerLabel,
  RESIDUAL_DECISIONS,
  ResidualChoices,
  residualDraft,
  ResidualDraft,
  residualErrorText,
  ResidualField,
  residualFieldId,
  residualItemId,
  residualProgress,
  residualReasonText,
  toRequest,
  withNextSurveyOf,
} from '../archiveView';
import { fullDate } from '../../HRDashboard/hmsFormat';
import { today } from '../dataSourcesView';
import { LEVEL_COLORS } from '../riskView';
import { TextField } from '../components/FormParts';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './Evaluation.module.css';

export const RESIDUAL_SECTION_ID = 'residual-risk';

type NextSurveyFieldsProps = {
  idOf: (field: 'nextSurveyBy' | 'owner') => string;
  value: { nextSurveyBy: string; ownerId: number | null };
  choices: ResidualChoices;
  errorFor: (field: ResidualField) => string | undefined;
  onChange: (next: { nextSurveyBy?: string; ownerId?: number | null }) => void;
};

/** The day and owner of the next survey, shared by every area left to it */
const NextSurveyFields = ({
  idOf,
  value,
  choices,
  errorFor,
  onChange,
}: NextSurveyFieldsProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const latest = choices.latestNextSurveyBy;
  return (
    <>
      <Field data-size="sm">
        <Label htmlFor={idOf('nextSurveyBy')}>
          {t('workEnvironmentArchive.residual.nextSurveyBy')}
        </Label>
        {latest && (
          <Paragraph data-size="xs" className={process.subtle}>
            {t('workEnvironmentArchive.residual.latestHelp', {
              date: fullDate(latest, i18n.language),
            })}
          </Paragraph>
        )}
        <Input
          id={idOf('nextSurveyBy')}
          type="date"
          min={today()}
          max={latest ?? undefined}
          value={value.nextSurveyBy}
          aria-invalid={Boolean(errorFor('nextSurveyBy')) || undefined}
          onChange={(event) => onChange({ nextSurveyBy: event.target.value })}
        />
        {errorFor('nextSurveyBy') && (
          <ValidationMessage>{errorFor('nextSurveyBy')}</ValidationMessage>
        )}
      </Field>
      <Field data-size="sm">
        <Label htmlFor={idOf('owner')}>
          {t('workEnvironmentArchive.residual.owner')}
        </Label>
        <Select
          id={idOf('owner')}
          value={value.ownerId ?? ''}
          aria-invalid={Boolean(errorFor('owner')) || undefined}
          onChange={(event) =>
            onChange({
              ownerId: event.target.value ? Number(event.target.value) : null,
            })
          }
        >
          <Select.Option value="" disabled>
            {t('workEnvironmentArchive.residual.chooseOwner')}
          </Select.Option>
          {choices.owners.map((owner) => (
            <Select.Option key={owner.id} value={owner.id}>
              {ownerLabel(owner, t)}
            </Select.Option>
          ))}
        </Select>
        {errorFor('owner') && (
          <ValidationMessage>{errorFor('owner')}</ValidationMessage>
        )}
      </Field>
    </>
  );
};

type FormProps = {
  surveyId: number;
  risk: ResidualRisk;
  choices: ResidualChoices;
  onSaved: () => void;
};

const DecisionForm = ({
  surveyId,
  risk,
  choices,
  onSaved,
}: FormProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<ResidualDraft>(
    residualDraft(risk.decision, choices),
  );
  const [shown, setShown] = useState<ResidualField[]>([]);
  const decide = useDecideResidualRiskMutation(surveyId);
  const follows = followsNextSurvey(risk, choices);
  const change = (next: Partial<ResidualDraft>) =>
    setDraft((current) => ({ ...current, ...next }));
  const errorFor = (field: ResidualField) =>
    shown.includes(field)
      ? residualErrorText(field, choices.latestNextSurveyBy, t, i18n.language)
      : undefined;

  const save = () => {
    const chosen = follows ? withNextSurveyOf(draft, choices.nextSurvey) : draft;
    const errors = draftErrors(chosen, {
      today: today(),
      latest: choices.latestNextSurveyBy,
    });
    setShown(errors);
    if (errors.length === 0)
      decide.mutate(
        { factorKey: risk.key, decision: toRequest(chosen) },
        { onSuccess: onSaved },
      );
  };

  return (
    <>
      <Fieldset data-size="sm" id={residualFieldId(risk.key, 'decision')}>
        <Fieldset.Legend>
          {t('workEnvironmentArchive.residual.question')}
        </Fieldset.Legend>
        <div className={classes.options}>
          {RESIDUAL_DECISIONS.map((option) => (
            <Radio
              key={option}
              name={`residual-${risk.key}`}
              value={option}
              label={t(`workEnvironmentArchive.residual.options.${option}`)}
              checked={draft.decision === option}
              aria-invalid={Boolean(errorFor('decision')) || undefined}
              onChange={() => change({ decision: option })}
            />
          ))}
        </div>
        {errorFor('decision') && (
          <ValidationMessage>{errorFor('decision')}</ValidationMessage>
        )}
      </Fieldset>
      {draft.decision === 'PLAN' && (
        <Paragraph data-size="sm" className={process.subtle}>
          {t('workEnvironmentArchive.residual.planText')}
        </Paragraph>
      )}
      {draft.decision === 'NEXT_SURVEY' && (
        <div className={classes.fields}>
          {follows && choices.nextSurvey ? (
            <Paragraph data-size="sm">
              {t(
                'workEnvironmentArchive.residual.sharedNextSurvey',
                nextSurveyText(choices.nextSurvey, t, i18n.language),
              )}
            </Paragraph>
          ) : (
            <NextSurveyFields
              idOf={(field) => residualFieldId(risk.key, field)}
              value={draft}
              choices={choices}
              errorFor={errorFor}
              onChange={change}
            />
          )}
          <TextField
            id={residualFieldId(risk.key, 'reason')}
            label={t('workEnvironmentArchive.residual.reason')}
            help={t('workEnvironmentArchive.residual.nextSurveyReasonHelp')}
            value={draft.reasons.NEXT_SURVEY}
            error={errorFor('reason')}
            onChange={(reason) =>
              change({ reasons: { ...draft.reasons, NEXT_SURVEY: reason } })
            }
          />
        </div>
      )}
      {draft.decision === 'ACCEPT' && (
        <>
          <TextField
            id={residualFieldId(risk.key, 'reason')}
            label={t('workEnvironmentArchive.residual.reason')}
            help={t('workEnvironmentArchive.residual.reasonHelp')}
            value={draft.reasons.ACCEPT}
            error={errorFor('reason')}
            onChange={(reason) =>
              change({ reasons: { ...draft.reasons, ACCEPT: reason } })
            }
          />
          <Field data-size="sm" className={classes.narrowField}>
            <Label htmlFor={residualFieldId(risk.key, 'acceptedBy')}>
              {t('workEnvironmentArchive.residual.acceptedBy')}
            </Label>
            <Select
              id={residualFieldId(risk.key, 'acceptedBy')}
              value={draft.acceptedBy ?? ''}
              aria-invalid={Boolean(errorFor('acceptedBy')) || undefined}
              onChange={(event) =>
                change({
                  acceptedBy:
                    choices.acceptedBy.find((body) => body === event.target.value) ??
                    null,
                })
              }
            >
              <Select.Option value="" disabled>
                {t('workEnvironmentArchive.residual.chooseAcceptedBy')}
              </Select.Option>
              {choices.acceptedBy.map((body) => (
                <Select.Option key={body} value={body}>
                  {t(
                    `workEnvironmentArchive.residual.acceptedByOptions.${body}`,
                  )}
                </Select.Option>
              ))}
            </Select>
            {errorFor('acceptedBy') && (
              <ValidationMessage>{errorFor('acceptedBy')}</ValidationMessage>
            )}
          </Field>
        </>
      )}
      {decide.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.residual.failed')}
          </Paragraph>
        </Alert>
      )}
      <div>
        <Button
          variant="secondary"
          data-size="sm"
          loading={decide.isPending}
          onClick={save}
        >
          {t('workEnvironmentArchive.residual.save')}
        </Button>
      </div>
    </>
  );
};

type ItemProps = {
  surveyId: number;
  risk: ResidualRisk;
  choices: ResidualChoices;
  canDecide: boolean;
};

const ResidualItem = ({
  surveyId,
  risk,
  choices,
  canDecide,
}: ItemProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(!risk.decision);
  const decision = risk.decision;

  return (
    <li id={residualItemId(risk.key)} className={classes.residualItem}>
      <div className={classes.residualTitle}>
        <span className={classes.focusTitle}>
          <span className="ds-label">{risk.name}</span>
          <Tag data-size="sm" data-color={LEVEL_COLORS[risk.level]}>
            {t(`workEnvironmentRisk.matrix.levels.${risk.level}`)}
          </Tag>
          {decision && (
            <Tag data-size="sm" data-color="success">
              {t('workEnvironmentArchive.residual.decidedTag')}
            </Tag>
          )}
        </span>
        <Paragraph data-size="sm" className={process.subtle}>
          {residualReasonText(risk, t)}
        </Paragraph>
      </div>
      {canDecide && editing ? (
        <DecisionForm
          surveyId={surveyId}
          risk={risk}
          choices={choices}
          onSaved={() => setEditing(false)}
        />
      ) : (
        decision && (
          <div className={classes.decided}>
            <Paragraph data-size="sm">
              {decisionText(documentedDecision(decision), t, i18n.language)}
            </Paragraph>
            {decision.earlier.length > 0 && (
              <div>
                <Paragraph data-size="xs" className={process.subtle}>
                  {t('workEnvironmentArchive.residual.earlierTitle')}
                </Paragraph>
                <ul className={classes.residualList}>
                  {decision.earlier.map((earlier) => (
                    <li key={earlier.decidedAt}>
                      <Paragraph data-size="xs" className={process.subtle}>
                        {earlierDecisionText(
                          documentedDecision(earlier),
                          t,
                          i18n.language,
                        )}
                      </Paragraph>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {canDecide && (
              <div>
                <Button
                  variant="tertiary"
                  className={classes.inlineAction}
                  data-size="sm"
                  onClick={() => setEditing(true)}
                >
                  {t('workEnvironmentArchive.residual.change')}
                </Button>
              </div>
            )}
          </div>
        )
      )}
    </li>
  );
};

type PanelProps = {
  surveyId: number;
  nextSurvey: NonNullable<ResidualChoices['nextSurvey']>;
  choices: ResidualChoices;
  canDecide: boolean;
};

/** One day and one owner for every area left to the next survey, changed here for all of them */
const NextSurveyPanel = ({
  surveyId,
  nextSurvey,
  choices,
  canDecide,
}: PanelProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState({
    nextSurveyBy: nextSurvey.nextSurveyBy,
    ownerId: nextSurvey.owner?.id ?? null,
  });
  const [shown, setShown] = useState<ResidualField[]>([]);
  const changeAll = useChangeNextSurveyMutation(surveyId);
  const errorFor = (field: ResidualField) =>
    shown.includes(field)
      ? residualErrorText(field, choices.latestNextSurveyBy, t, i18n.language)
      : undefined;

  const save = () => {
    const errors = nextSurveyErrors(value, {
      today: today(),
      latest: choices.latestNextSurveyBy,
    });
    setShown(errors);
    if (errors.length === 0)
      changeAll.mutate(value, { onSuccess: () => setEditing(false) });
  };

  return (
    <div className={classes.decided} aria-labelledby="next-survey-heading" role="group">
      <Heading level={3} data-size="2xs" id="next-survey-heading">
        {t('workEnvironmentArchive.residual.nextSurveyTitle')}
      </Heading>
      <Paragraph data-size="sm">
        {t(
          'workEnvironmentArchive.residual.nextSurveySummary',
          nextSurveyText(nextSurvey, t, i18n.language),
        )}
      </Paragraph>
      {canDecide && editing && (
        <>
          <div className={classes.fields}>
            <NextSurveyFields
              idOf={(field) => `next-survey-${field}`}
              value={value}
              choices={choices}
              errorFor={errorFor}
              onChange={(next) => setValue((current) => ({ ...current, ...next }))}
            />
          </div>
          {changeAll.isError && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">
                {t('workEnvironmentArchive.residual.nextSurveyFailed')}
              </Paragraph>
            </Alert>
          )}
          <div className={classes.archiveActions}>
            <Button
              variant="secondary"
              data-size="sm"
              loading={changeAll.isPending}
              onClick={save}
            >
              {t('workEnvironmentArchive.residual.saveNextSurvey')}
            </Button>
            <Button variant="tertiary" data-size="sm" onClick={() => setEditing(false)}>
              {t('workEnvironmentRisk.assessment.cancel')}
            </Button>
          </div>
        </>
      )}
      {canDecide && !editing && (
        <div>
          <Button
            variant="tertiary"
            className={classes.inlineAction}
            data-size="sm"
            onClick={() => setEditing(true)}
          >
            {t('workEnvironmentArchive.residual.changeNextSurvey')}
          </Button>
        </div>
      )}
    </div>
  );
};

type Props = {
  surveyId: number;
  risks: ResidualRisk[];
  choices: ResidualChoices;
  canDecide: boolean;
};

/** Every area that is still high or has become worse gets a decision before the work is archived */
export const ResidualRiskCard = ({
  surveyId,
  risks,
  choices,
  canDecide,
}: Props): ReactElement => {
  const { t } = useTranslation();
  return (
    <section
      id={RESIDUAL_SECTION_ID}
      aria-labelledby="residual-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="residual-heading">
            {t('workEnvironmentArchive.residual.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentArchive.residual.intro')}
          </Paragraph>
        </div>
        {risks.length === 0 ? (
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.residual.none')}
          </Paragraph>
        ) : (
          <>
            <Paragraph
              data-size="sm"
              role="status"
              className={classes.progress}
            >
              {t(
                'workEnvironmentArchive.residual.progress',
                residualProgress(risks),
              )}
            </Paragraph>
            <ul className={classes.residualList}>
              {risks.map((risk) => (
                <ResidualItem
                  key={risk.key}
                  surveyId={surveyId}
                  risk={risk}
                  choices={choices}
                  canDecide={canDecide}
                />
              ))}
            </ul>
            {choices.nextSurvey && (
              <NextSurveyPanel
                key={`${choices.nextSurvey.nextSurveyBy}-${choices.nextSurvey.owner?.id}`}
                surveyId={surveyId}
                nextSurvey={choices.nextSurvey}
                choices={choices}
                canDecide={canDecide}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};
